import React from 'react';
import { motion } from 'framer-motion';
import { Phone, MessageCircle, Shield, Clock, Star, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { COMPANY_INFO } from '../mock';

export const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 pb-24 overflow-hidden bg-[#0f172a]">
      {/* Animated Background Gradient */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 -left-40 w-[600px] h-[600px] bg-[#22c55e]/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] bg-[#22c55e]/10 rounded-full blur-[100px]" />
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text Content */}
          <motion.div 
            className="space-y-10"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.4, 0.25, 1] }}
          >
            {/* Trust Badge */}
            <motion.div 
              className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/5 backdrop-blur-md border border-white/10 rounded-full"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Shield className="w-5 h-5 text-[#22c55e]" />
              <span className="text-white/80 text-sm font-medium">Unabhängige Sachverständige</span>
            </motion.div>

            {/* Main Headline */}
            <div className="space-y-4">
              <motion.h1 
                className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.7 }}
              >
                Ihr Recht
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10 bg-gradient-to-r from-[#22c55e] to-[#4ade80] bg-clip-text text-transparent">
                    nach dem Unfall
                  </span>
                  {/* Underline Effect */}
                  <span className="absolute -bottom-2 left-0 right-0 h-3 bg-[#22c55e]/20 rounded-full -skew-x-3" />
                </span>
              </motion.h1>
              
              <motion.p 
                className="text-xl lg:text-2xl text-slate-400 font-medium max-w-lg leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                Unabhängiges Kfz-Gutachten in 30 Minuten vor Ort.
                <br />
                <span className="text-white/70">Kostenlos bei unverschuldetem Unfall.</span>
              </motion.p>
            </div>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <a href={`tel:${COMPANY_INFO.phone}`} className="group">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-[#22c55e] hover:bg-[#16a34a] text-white text-lg px-10 py-7 rounded-full font-semibold shadow-[0_0_30px_rgba(34,197,94,0.4)] hover:shadow-[0_0_50px_rgba(34,197,94,0.5)] hover:-translate-y-1 transition-all duration-300 flex items-center gap-3"
                >
                  <Phone className="w-5 h-5" />
                  Jetzt kostenlos anrufen
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full sm:w-auto border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 text-lg px-10 py-7 rounded-full font-semibold transition-all duration-300"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div 
              className="flex flex-wrap gap-6 pt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              {[
                { icon: Clock, text: 'Termin in 30 Min' },
                { icon: Shield, text: 'Unabhängig & neutral' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-slate-400">
                  <div className="w-8 h-8 rounded-full bg-[#22c55e]/10 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-[#22c55e]" />
                  </div>
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Modern Image Card */}
          <motion.div 
            className="relative hidden lg:block"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: [0.25, 0.4, 0.25, 1] }}
          >
            {/* Main Image Container with Custom Shape */}
            <div className="relative">
              {/* Floating Stats Cards */}
              <motion.div 
                className="absolute -top-6 -left-6 z-20 bg-white rounded-2xl p-5 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.3)]"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <p className="text-3xl font-black text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>Schnell</p>
                <p className="text-slate-500 text-sm font-medium">Termin vor Ort</p>
              </motion.div>

              <motion.div 
                className="absolute -bottom-4 -right-4 z-20 bg-[#22c55e] rounded-2xl p-5 shadow-[0_20px_40px_-12px_rgba(34,197,94,0.4)]"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              >
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>24h</p>
                <p className="text-white/80 text-sm font-medium">Gutachten fertig</p>
              </motion.div>

              {/* Image with Custom Mask */}
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white/10">
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/60 via-transparent to-transparent z-10" />
                <img 
                  src="https://customer-assets.emergentagent.com/job_gutachten-direkt/artifacts/kpm3hznz_7147C380-E46A-4E1B-9F66-2AF916858612.png"
                  alt="Professioneller Kfz-Sachverständiger dokumentiert Unfallschaden"
                  className="w-full h-[550px] object-cover"
                />
                
                {/* Bottom Info Bar */}
                <div className="absolute bottom-0 left-0 right-0 z-20 p-6 bg-gradient-to-t from-black/90 to-transparent">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#22c55e] font-semibold text-sm uppercase tracking-wider">Professionell & Digital</p>
                      <p className="text-white text-lg font-medium">Moderne Schadensdokumentation</p>
                    </div>
                    <div className="flex -space-x-2">
                      {['🇩🇪', '🚗', '📱'].map((emoji, i) => (
                        <div key={i} className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-xl border-2 border-white/20">
                          {emoji}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};
